/**
 * Blog SEO Renforteo — articles longue-traîne alignés sur les requêtes Google Search Console.
 *
 * Chaque article est structuré pour :
 *  - un titre riche en mot-clé (h1 + <title>)
 *  - une meta_desc ≤ 158 caractères
 *  - un plan h2/h3 clair (Google adore la structure)
 *  - une FAQ pour rich snippets
 *  - un CTA vers la page métier / configurateur
 *
 * Format sections :
 *   { type: "h2" | "h3" | "p" | "ul" | "ol" | "quote" | "cta", text | items | to | label }
 */

export const BLOG_ARTICLES = {
    "devenir-livreur-monteur-auto-entrepreneur": {
        slug: "devenir-livreur-monteur-auto-entrepreneur",
        h1: "Comment devenir livreur-monteur en auto-entrepreneur en 2026",
        title: "Devenir livreur-monteur en auto-entrepreneur : guide complet 2026",
        meta_desc:
            "Devenir livreur-monteur de meubles en auto-entrepreneur : SIRET, matériel, tarifs, clients (Ikea, Conforama, But) et missions. Guide pas à pas Renforteo.",
        category: "Livraison & Logistique",
        published_at: "2026-02-24",
        read_time_min: 6,
        hero_img: "https://images.pexels.com/photos/4491881/pexels-photo-4491881.jpeg?auto=compress&cs=tinysrgb&w=1400",
        keywords: ["livreur monteur", "livreur monteur de meuble", "auto-entrepreneur livreur"],
        related_slug: "livreur-monteur",
        related_route: "/metiers/livreur-monteur",
        excerpt:
            "Le métier de livreur-monteur (livraison + montage de meubles chez le client) explose depuis l'essor du e-commerce d'ameublement. Voici comment en faire votre activité principale ou secondaire — sans agence d'intérim.",
        sections: [
            {
                type: "p",
                text: "Le métier de livreur-monteur (aussi appelé « livreur monteur de meubles ») consiste à livrer un meuble ET l'installer chez le client final. Vous déballez, vous montez selon la notice, vous positionnez le meuble à l'endroit choisi, et vous emportez les emballages. Un service premium très recherché par Ikea, Conforama, But, Maisons du Monde, Alinéa et les pure-players e-commerce.",
            },
            {
                type: "h2",
                text: "Pourquoi choisir le statut auto-entrepreneur ?",
            },
            {
                type: "p",
                text: "Le statut d'auto-entrepreneur (aussi appelé micro-entrepreneur) est le plus adapté à ce métier : création gratuite en 10 minutes, comptabilité ultra-simple (livre des recettes), et cotisations calculées uniquement sur ce que vous facturez (environ 21,2 % du CA en BIC prestations de services). Aucun risque financier tant que vous ne facturez pas.",
            },
            {
                type: "h2",
                text: "Les 4 étapes pour démarrer",
            },
            {
                type: "ol",
                items: [
                    "**Créer votre SIRET** — direction formalites.entreprises.gouv.fr, choisir « micro-entrepreneur », activité BIC « livraison à domicile et montage de meubles » (code APE 4941C ou 4322A selon volume). Gratuit, réponse INSEE en 8 à 15 jours.",
                    "**Souscrire une RC Pro** — obligatoire pour couvrir les dommages accidentels (meuble abîmé, mur rayé, dégât des eaux). Comptez 15 à 25 €/mois chez Hiscox, AMV Pro ou Wemind.",
                    "**Équipement minimum** — une visseuse à batterie 18V, un jeu de clés Allen métriques + impériales, un mètre, un niveau à bulle, des gants de protection, des tapis de sol. Budget total : 150 à 300 €.",
                    "**Trouver vos premiers clients** — inscrivez-vous sur Renforteo (SIRET vérifié + KBIS + CNI par IA), recevez uniquement les missions dans votre rayon géographique, choisissez celles qui vous conviennent.",
                ],
            },
            {
                type: "h2",
                text: "Combien gagne un livreur-monteur ?",
            },
            {
                type: "p",
                text: "En 2026, le tarif horaire moyen d'un livreur-monteur en auto-entrepreneur se situe entre 18 et 24 €/h HT facturés au client. Pour une mission type (2 h de livraison + 3 h de montage = 5 h), vous facturez donc 90 à 120 € HT. Sur Renforteo, les frais plateforme sont de 8 % — vous conservez environ 82,80 à 110,40 € par mission.",
            },
            {
                type: "p",
                text: "Un livreur-monteur expérimenté enchaîne 2 à 4 missions par jour (selon la complexité) : un meuble Ikea standard demande 45 min à 1 h, une cuisine équipée 4 à 6 h. En moyenne, comptez 400 à 700 € de chiffre d'affaires par jour travaillé.",
            },
            {
                type: "h2",
                text: "Les qualités indispensables",
            },
            {
                type: "ul",
                items: [
                    "**Aisance en bricolage** — savoir lire une notice, utiliser une visseuse, distinguer une vis M6 d'une M8",
                    "**Condition physique** — un canapé 3 places dans un immeuble sans ascenseur, ça se mérite",
                    "**Sens du service** — le client note votre prestation, votre présentation compte autant que votre technicité",
                    "**Ponctualité** — les créneaux de 2 h chez le client sont serrés, un retard casse toute la tournée",
                    "**Véhicule adapté** — utilitaire type Kangoo, Trafic ou Master (souvent fourni ou loué par le client pour Ikea / Conforama)",
                ],
            },
            {
                type: "h2",
                text: "Faut-il un permis particulier ?",
            },
            {
                type: "p",
                text: "Le permis B suffit pour les véhicules de moins de 3,5 tonnes (utilitaire standard). Au-delà, il faut le permis C. Aucune formation obligatoire pour le montage — mais suivre une formation Ikea « TaskRabbit » ou équivalent renforce votre CV.",
            },
            {
                type: "cta",
                to: "/candidat/inscription",
                label: "Rejoindre Renforteo comme livreur-monteur",
                subtext: "Inscription gratuite · Validation profil sous 24 h · Recevez les premières missions dès la validation.",
            },
        ],
        faqs: [
            {
                q: "Quel est le tarif horaire d'un livreur-monteur en 2026 ?",
                a: "Entre 18 et 24 €/h HT facturés au client. Un livreur-monteur expérimenté génère 400 à 700 € de CA par jour travaillé.",
            },
            {
                q: "Faut-il un permis spécial pour être livreur-monteur ?",
                a: "Non, le permis B suffit pour les utilitaires de moins de 3,5 t. Au-delà, il faut le permis C. Le véhicule est souvent fourni par le client (Ikea, Conforama).",
            },
            {
                q: "Combien coûte la création d'une auto-entreprise pour livreur-monteur ?",
                a: "C'est 100 % gratuit sur le site officiel formalites.entreprises.gouv.fr. Comptez 10 min pour remplir le formulaire et 8 à 15 jours pour recevoir votre numéro SIRET.",
            },
            {
                q: "Puis-je combiner livraison classique et livraison-montage ?",
                a: "Oui, votre statut auto-entrepreneur autorise plusieurs prestations dans le même code APE. Sur Renforteo, vous cochez les 2 métiers dans votre profil et vous recevez les missions correspondantes.",
            },
        ],
    },

    "combien-gagne-plongeur-traiteur": {
        slug: "combien-gagne-plongeur-traiteur",
        h1: "Combien gagne un plongeur professionnel en traiteur événementiel ?",
        title: "Combien gagne un plongeur traiteur ? Tarifs 2026 | Renforteo",
        meta_desc:
            "Combien gagne un plongeur professionnel en traiteur événementiel en 2026 ? Tarifs horaires, missions types, comparatif intérim vs auto-entrepreneur.",
        category: "Restauration",
        published_at: "2026-02-24",
        read_time_min: 5,
        hero_img: "https://images.pexels.com/photos/4252140/pexels-photo-4252140.jpeg?auto=compress&cs=tinysrgb&w=1400",
        keywords: ["plongeur professionnel traiteur", "plongeur salaire", "plongeur événementiel"],
        related_slug: "plongeur",
        related_route: "/metiers/plongeur",
        excerpt:
            "Métier accessible sans diplôme, la plonge est massivement demandée par les traiteurs événementiels (cocktails, mariages, séminaires). Voici la fourchette réelle de rémunération en 2026, et comment maximiser votre CA en auto-entrepreneur.",
        sections: [
            {
                type: "p",
                text: "La plonge en traiteur événementiel n'a rien à voir avec la plonge en restaurant classique. Les volumes sont plus importants (200 à 1 000 couverts en une soirée), les cadences plus intenses, mais les tarifs horaires sont eux aussi supérieurs. C'est aujourd'hui l'un des métiers les plus recherchés par les traiteurs de la région lyonnaise, valentinoise et grenobloise.",
            },
            {
                type: "h2",
                text: "Tarif horaire d'un plongeur traiteur en 2026",
            },
            {
                type: "p",
                text: "En 2026, un plongeur professionnel intervenant chez un traiteur événementiel est rémunéré entre 16 et 21 €/h HT facturés au client. Le tarif varie selon 4 critères :",
            },
            {
                type: "ul",
                items: [
                    "**Le type d'événement** — mariage haut de gamme : 19-21 €/h · cocktail entreprise : 17-19 €/h · séminaire standard : 16-18 €/h",
                    "**L'horaire** — nuit (>22h) : +25 % · dimanche : +50 % · jour férié : +50 %",
                    "**Le volume** — >500 couverts génère souvent une prime de rendement",
                    "**L'ancienneté** — un « Super Talent » sur Renforteo (10+ missions, note ≥ 4,7/5) accède aux missions premium",
                ],
            },
            {
                type: "h2",
                text: "Simulation de revenus mensuels",
            },
            {
                type: "p",
                text: "Prenons un plongeur qui accepte 3 missions par semaine chez des traiteurs valentinois :",
            },
            {
                type: "ul",
                items: [
                    "3 missions × 6 h × 18 €/h = 324 € par semaine",
                    "Sur 4 semaines : **1 296 € de CA mensuel**",
                    "Frais Renforteo (15 %) : -155 €",
                    "Cotisations micro-entrepreneur (~21,2 %) : -274 €",
                    "**Revenu net avant impôt : ~867 €/mois**",
                ],
            },
            {
                type: "p",
                text: "En complément d'une autre activité ou pour financer un projet, c'est une source de revenu attractif — sans agence d'intérim qui prélève 30 à 50 % au passage.",
            },
            {
                type: "h2",
                text: "Auto-entrepreneur vs intérim : la comparaison",
            },
            {
                type: "p",
                text: "Chez une agence d'intérim classique, un plongeur touche environ 12,50 € net/h après charges patronales et marge de l'agence. En auto-entrepreneur via Renforteo, sur un tarif HT client de 18 €/h, vous conservez environ 14 € net après frais plateforme et cotisations. Avantage supplémentaire : vous choisissez vos missions, vos horaires et vos clients.",
            },
            {
                type: "h2",
                text: "Comment démarrer rapidement ?",
            },
            {
                type: "ol",
                items: [
                    "Créer votre SIRET auto-entrepreneur (10 min, gratuit, sur formalites.entreprises.gouv.fr)",
                    "S'inscrire sur Renforteo avec KBIS + CNI + RIB (validation sous 24 h ouvrées)",
                    "Cocher « Plongeur » comme métier principal et éventuellement « Commis » ou « Manutentionnaire » en secondaire pour maximiser les propositions",
                    "Recevoir les missions dans votre rayon géographique et accepter celles qui vous conviennent",
                ],
            },
            {
                type: "cta",
                to: "/candidat/inscription",
                label: "Commencer comme plongeur traiteur",
                subtext: "Inscription 100 % gratuite · 0 abonnement · Vous choisissez vos missions.",
            },
        ],
        faqs: [
            {
                q: "Combien gagne un plongeur traiteur en auto-entrepreneur ?",
                a: "Entre 16 et 21 €/h HT selon l'événement, avec majorations nuit (+25 %) et dimanche (+50 %). En moyenne, un plongeur régulier génère 800 à 1 500 € net par mois en complément d'activité.",
            },
            {
                q: "Faut-il un diplôme pour être plongeur ?",
                a: "Non, aucune formation n'est requise. Une bonne condition physique et la ponctualité suffisent pour démarrer. Une formation HACCP peut être un plus pour accéder aux missions premium.",
            },
            {
                q: "Quelle différence entre plongeur restaurant et plongeur traiteur ?",
                a: "Les traiteurs travaillent sur des événements ponctuels (mariages, cocktails, séminaires) avec des volumes plus importants (200 à 1 000 couverts). Les tarifs horaires sont donc supérieurs (16-21 €/h vs 15-17 €/h en restaurant classique).",
            },
        ],
    },

    "devenir-serveur-lyon-guide": {
        slug: "devenir-serveur-lyon-guide",
        h1: "Devenir serveur à Lyon en 2026 : missions, tarifs et alternatives à l'intérim",
        title: "Devenir serveur à Lyon : guide complet, tarifs, alternatives intérim | Renforteo",
        meta_desc:
            "Guide 2026 pour devenir serveur à Lyon en auto-entrepreneur : tarifs (16-22 €/h), missions événementielles, quartiers actifs, alternative à l'intérim.",
        category: "Restauration",
        published_at: "2026-02-24",
        read_time_min: 6,
        hero_img: "/hero/lyon-fourviere.jpg",
        keywords: ["serveur Lyon", "emploi serveur Lyon", "intérim serveur Lyon"],
        related_slug: "serveur-lyon",
        related_route: "/recrutement-serveur-lyon",
        excerpt:
            "Lyon, capitale gastronomique, ne cesse de rechercher des serveurs qualifiés. Voici comment en faire votre métier (ponctuel ou principal) en 2026, avec une alternative moderne à l'intérim.",
        sections: [
            {
                type: "p",
                text: "Avec plus de 4 000 restaurants, 300 hôtels et des dizaines de rooftops entre la Presqu'île et la Confluence, Lyon est l'une des villes françaises où la demande en serveurs est la plus soutenue. Fête des Lumières, Nuits de Fourvière, Sirha, événements OL au Groupama Stadium, matchs LOU au Matmut Stadium : les pics d'activité s'enchaînent toute l'année.",
            },
            {
                type: "h2",
                text: "Quartiers et types d'établissements",
            },
            {
                type: "ul",
                items: [
                    "**Presqu'île (Bellecour, Terreaux)** — restaurants bistronomiques, brasseries traditionnelles lyonnaises (bouchons), rooftops",
                    "**Confluence** — restaurants gastronomiques 5*, hôtels premium (Villa Maïa, Château Perrache), événements corporate",
                    "**Part-Dieu** — hôtels d'affaires (Radisson, Novotel, Hilton), traiteurs pour séminaires",
                    "**Vieux-Lyon** — bouchons traditionnels, restaurants historiques classés",
                    "**6ᵉ arrondissement** — restaurants étoilés, banquets de mariage, Parc de la Tête d'Or",
                ],
            },
            {
                type: "h2",
                text: "Tarifs horaires 2026 à Lyon",
            },
            {
                type: "p",
                text: "Un serveur en auto-entrepreneur à Lyon facture aujourd'hui entre 16 et 22 €/h HT au client, selon les critères suivants :",
            },
            {
                type: "ul",
                items: [
                    "**Serveur standard** — brunch, service midi, brasserie : 16-18 €/h",
                    "**Serveur événementiel** — mariage, cocktail : 18-20 €/h",
                    "**Chef de rang** — restaurant gastronomique, dressage à la française : 20-24 €/h",
                    "**Majorations** — nuit (+25 %) · dimanche (+50 %) · jour férié (+50 %) · urgent (+10 %)",
                ],
            },
            {
                type: "h2",
                text: "Intérim serveur Lyon vs auto-entrepreneur",
            },
            {
                type: "p",
                text: "Historiquement, les serveurs lyonnais passent par les agences d'intérim (Adecco, Manpower, Randstad Hospitality). Le problème : la marge d'intérim (30 à 50 % du montant facturé au client) rogne fortement votre rémunération nette, et vous êtes rarement au courant du tarif exact facturé.",
            },
            {
                type: "p",
                text: "L'alternative Renforteo est simple : vous êtes auto-entrepreneur, vous facturez directement le client, les frais plateforme sont fixés à 8 % (mentionnés dans le contrat) et vous voyez le montant exact que le client paie. La transparence tarifaire vous permet d'accéder aux mêmes clients (hôtels, restaurants, traiteurs) sans intermédiaire opaque.",
            },
            {
                type: "h2",
                text: "Comment démarrer à Lyon ?",
            },
            {
                type: "ol",
                items: [
                    "Créer votre SIRET auto-entrepreneur (10 min, gratuit sur le site officiel du gouvernement)",
                    "S'inscrire sur Renforteo avec les 4 pièces indispensables (SIRET, KBIS ou fiche INSEE, carte d'identité recto+verso, RIB)",
                    "Cocher « Serveur / Serveuse » comme métier principal + éventuellement « Chef de rang », « Barman » ou « Hôte » en métiers secondaires",
                    "Définir votre rayon de mobilité (25 km centré sur Lyon couvre Villeurbanne, Caluire, Bron, Vénissieux, Écully…)",
                    "Recevoir les missions qui correspondent à votre profil et choisir librement",
                ],
            },
            {
                type: "cta",
                to: "/candidat/inscription",
                label: "Devenir serveur à Lyon avec Renforteo",
                subtext: "Alternative directe à l'intérim · Sans engagement · Paiement sous 7 jours.",
            },
        ],
        faqs: [
            {
                q: "Combien gagne un serveur à Lyon en 2026 ?",
                a: "Entre 16 et 22 €/h HT selon la qualification (serveur simple, événementiel, chef de rang), avec majorations nuit/dimanche/férié. Un serveur régulier peut générer 1 500 à 2 500 € net par mois en complément d'activité.",
            },
            {
                q: "Peut-on être serveur à Lyon sans passer par une agence d'intérim ?",
                a: "Oui, en devenant auto-entrepreneur et en s'inscrivant sur des plateformes de mise en relation comme Renforteo. Vous facturez directement le client, sans marge d'intérim qui rogne votre rémunération.",
            },
            {
                q: "Quels documents fournir pour devenir serveur à Lyon ?",
                a: "SIRET auto-entrepreneur (à créer gratuitement en 10 min), KBIS ou fiche INSEE, carte d'identité recto+verso, RIB, justificatif de domicile de moins de 3 mois.",
            },
        ],
    },

    "renfort-service-evenementiel-guide": {
        slug: "renfort-service-evenementiel-guide",
        h1: "Renfort service événementiel : quand et comment recourir à un professionnel ?",
        title: "Renfort service événementiel : guide 2026 pour les entreprises | Renforteo",
        meta_desc:
            "Guide entreprise 2026 pour recourir à un renfort service événementiel : quand faire appel, tarifs, obligations légales, alternative à l'intérim.",
        category: "Événementiel B2B",
        published_at: "2026-02-24",
        read_time_min: 5,
        hero_img: "https://images.pexels.com/photos/1128678/pexels-photo-1128678.jpeg?auto=compress&cs=tinysrgb&w=1400",
        keywords: ["renfort service", "renfort événementiel", "extra restauration"],
        related_slug: "entreprises",
        related_route: "/entreprises",
        excerpt:
            "Cocktail d'entreprise, mariage, séminaire, ouverture de restaurant : quand faut-il faire appel à un renfort service, et comment maximiser la qualité tout en maîtrisant votre budget ?",
        sections: [
            {
                type: "p",
                text: "Le « renfort service » désigne le recours ponctuel à des professionnels indépendants externes (serveurs, hôtes d'accueil, plongeurs, barmen, agents d'entretien) pour couvrir un pic d'activité, un événement exceptionnel ou l'absence d'un membre de votre équipe. C'est une pratique historique en restauration, hôtellerie et événementiel — et une manière très efficace de préserver la qualité de service sans démarches d'embauche.",
            },
            {
                type: "h2",
                text: "Les 5 situations où le renfort service est indispensable",
            },
            {
                type: "ol",
                items: [
                    "**Événement ponctuel** — cocktail d'entreprise, mariage, séminaire, ouverture de restaurant. Le personnel permanent ne suffit pas à couvrir un volume 3 à 10 fois supérieur à la normale.",
                    "**Absence non planifiée** — arrêt maladie d'un serveur clé, congé maternité, démission soudaine. Il faut un remplaçant qualifié en 24 h.",
                    "**Saisonnalité** — juillet-août en zone touristique (Valence, Grenoble Alpes), Noël, Fête des Lumières à Lyon, événements corporate de fin d'année.",
                    "**Pic hebdomadaire** — brunch dominical, service du samedi soir, mariage un week-end sur deux.",
                    "**Test avant collaboration régulière** — vous hésitez sur un profil ? Faites-le intervenir sur 2 ou 3 missions ponctuelles avant de le prioriser en favori pour les missions suivantes.",
                ],
            },
            {
                type: "h2",
                text: "Quelle solution choisir ?",
            },
            {
                type: "ul",
                items: [
                    "**Agence d'intérim** — solution historique, mais marge élevée (30 à 50 %) et personnel peu fidélisé. Le personnel est employé par l'agence, vous payez cher pour peu de flexibilité.",
                    "**Plateforme d'auto-entrepreneurs** (type Renforteo) — frais plateforme de 15 % côté entreprise + 8 % côté professionnel, personnel indépendant, contrat de prestation de services automatique, paiement sécurisé Stripe.",
                    "**Recrutement direct via annonces** — solution la moins chère mais chronophage (tri des CV, entretiens, vérifications documentaires), risque juridique élevé si les papiers ne sont pas contrôlés.",
                ],
            },
            {
                type: "h2",
                text: "Tarifs indicatifs 2026",
            },
            {
                type: "ul",
                items: [
                    "**Serveur / Serveuse** — 16-22 €/h HT",
                    "**Chef de rang / Maître d'hôtel** — 20-26 €/h HT",
                    "**Barman** — 17-24 €/h HT",
                    "**Hôte / Hôtesse d'accueil** — 14-20 €/h HT",
                    "**Plongeur** — 16-21 €/h HT",
                    "**Manutentionnaire / logistique** — 15-19 €/h HT",
                ],
            },
            {
                type: "p",
                text: "Ajoutez les frais plateforme (15 % sur Renforteo côté entreprise, contre 30 à 50 % en intérim classique) et la TVA éventuelle (0 % si le pro est en franchise base art. 293 B CGI, 20 % sinon) pour obtenir le total TTC. Aucun frais caché, aucun engagement, aucun abonnement.",
            },
            {
                type: "h2",
                text: "Les 3 réflexes juridiques pour un renfort service sans risque",
            },
            {
                type: "ol",
                items: [
                    "**Vérifier le SIRET** du prestataire — obligatoire depuis 2020 pour éviter le travail dissimulé (Renforteo le fait pour vous)",
                    "**Demander l'attestation de vigilance** au-delà de 5 000 € HT cumulés annuels avec le même prestataire — obligation Code du travail art. L8222-1",
                    "**Signer un contrat de prestation de services** (jamais un contrat de travail) — Renforteo génère automatiquement ce contrat à la sélection du candidat",
                ],
            },
            {
                type: "cta",
                to: "/devis",
                label: "Obtenir un devis de renfort service",
                subtext: "Devis TTC affiché en direct · Aucun frais caché · Sans inscription obligatoire.",
            },
        ],
        faqs: [
            {
                q: "Qu'est-ce qu'un renfort service ?",
                a: "Un renfort service désigne le recours ponctuel à un professionnel externe (serveur, hôte, plongeur, manutentionnaire, personnel de nettoyage) pour couvrir un pic d'activité, un événement ou l'absence d'un membre de votre équipe. Contrat de prestation de services obligatoire pour éviter la requalification en contrat de travail.",
            },
            {
                q: "Combien coûte un renfort service en 2026 ?",
                a: "Entre 14 et 26 €/h HT selon le métier, plus 15 % de frais plateforme côté entreprise. TVA appliquée selon le statut du professionnel (0 % si franchise base art. 293 B CGI, sinon 20 %). Sur Renforteo, le montant TTC est affiché en direct dans le configurateur avant paiement, aucun frais caché.",
            },
            {
                q: "Faut-il faire une déclaration sociale pour un renfort service ?",
                a: "Non. En passant par un auto-entrepreneur immatriculé (SIRET vérifié), vous êtes donneur d'ordre et non employeur. Aucune déclaration sociale, aucune charge patronale, aucun bulletin de paie — le prestataire facture directement et gère seul sa fiscalité.",
            },
        ],
    },
};

export const BLOG_SLUGS = Object.keys(BLOG_ARTICLES);
export const BLOG_LIST = Object.values(BLOG_ARTICLES).map((a) => ({
    slug: a.slug,
    h1: a.h1,
    excerpt: a.excerpt,
    category: a.category,
    published_at: a.published_at,
    read_time_min: a.read_time_min,
    hero_img: a.hero_img,
}));
